import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, switchMap, catchError, of, map } from 'rxjs';
import {
    CreateInvitationsRequest,
    CreateInvitationsResponse,
    InvitationPublicInfo,
    AcceptInvitationResponse,
    InvitationInput,
    InvitationDocument,
} from '@newgenus/common';
import firebase from 'firebase/compat/app';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { environment } from '../../../environments/environment';

/**
 * Frontend service for managing organization invitations.
 *
 * Communicates with the backend invitation API functions:
 * - invitationsCreate: Create new invitations
 * - invitationsGetByToken: Get invitation details (public, no auth)
 * - invitationsAccept: Accept an invitation
 * - invitationsDecline: Decline an invitation
 */
@Injectable({
    providedIn: 'root',
})
export class InvitationService {
    private readonly suffix = '-default';

    constructor(
        private http: HttpClient,
        private db: AngularFirestore
    ) {}

    // ================================================================
    // API URL Construction
    // ================================================================

    /**
     * Get the base URL for API calls based on environment configuration.
     */
    private getApiBaseUrl(): string {
        if (environment.useEmulators) {
            return environment.emulator_config.url;
        }
        return environment.gcp.baseUrl;
    }

    /**
     * Build the full URL for an invitation API function.
     */
    private getApiUrl(functionName: string): string {
        return `${this.getApiBaseUrl()}invitations${functionName}${this.suffix}`;
    }

    /**
     * Get the frontend base URL for invitation links.
     * This is used to construct the acceptance URL sent in emails.
     */
    getFrontendBaseUrl(): string {
        if (environment.production) {
            // Production: use the deployed URL
            // TODO: Update this when production domain is confirmed
            return `https://${environment.env === 'production' ? 'app' : environment.env}.newgenus.org`;
        }
        // Development: use localhost
        return 'http://localhost:4200';
    }

    // ================================================================
    // Auth Helpers
    // ================================================================

    /**
     * Get the current user's ID token for authenticated API calls.
     */
    private async getIdToken(): Promise<string> {
        const user = firebase.auth().currentUser;
        if (!user) {
            throw new Error('User is not authenticated');
        }
        return user.getIdToken();
    }

    /**
     * Create HttpHeaders with the Bearer token.
     */
    private async getAuthHeaders(): Promise<HttpHeaders> {
        const token = await this.getIdToken();
        return new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        });
    }

    // ================================================================
    // API Methods
    // ================================================================

    /**
     * Create invitations for one or more email addresses.
     *
     * @param orgKey The organization key
     * @param invitations Array of invitation inputs (email + optional positions/hats)
     * @returns Observable with the creation results
     */
    createInvitations(orgKey: string, invitations: InvitationInput[]): Observable<CreateInvitationsResponse> {
        const url = this.getApiUrl('Create');
        const body: CreateInvitationsRequest = { orgKey, invitations };

        return from(this.getAuthHeaders()).pipe(
            switchMap((headers) => this.http.post<CreateInvitationsResponse>(url, body, { headers })),
            catchError((error) => {
                console.error('InvitationService > createInvitations error:', error);
                throw error;
            })
        );
    }

    /**
     * Get invitation details by token.
     * This is a PUBLIC endpoint - no authentication required.
     * Used by the acceptance page to display invitation info.
     *
     * @param token The invitation token from the email link
     * @returns Observable with the public invitation info
     */
    getInvitationByToken(token: string): Observable<InvitationPublicInfo> {
        const url = `${this.getApiUrl('GetByToken')}?token=${encodeURIComponent(token)}`;

        return this.http.get<InvitationPublicInfo>(url).pipe(
            catchError((error) => {
                console.error('InvitationService > getInvitationByToken error:', error);
                throw error;
            })
        );
    }

    /**
     * Accept an invitation. User must be logged in.
     *
     * @param token The invitation token
     * @returns Observable with the acceptance result
     */
    acceptInvitation(token: string): Observable<AcceptInvitationResponse> {
        const url = this.getApiUrl('Accept');

        return from(this.getAuthHeaders()).pipe(
            switchMap((headers) => this.http.post<AcceptInvitationResponse>(url, { token }, { headers })),
            catchError((error) => {
                console.error('InvitationService > acceptInvitation error:', error);
                throw error;
            })
        );
    }

    /**
     * Decline an invitation. User must be logged in.
     *
     * @param token The invitation token
     * @returns Observable with the decline result
     */
    declineInvitation(token: string): Observable<{ success: boolean; message: string }> {
        const url = this.getApiUrl('Decline');

        return from(this.getAuthHeaders()).pipe(
            switchMap((headers) => this.http.post<{ success: boolean; message: string }>(url, { token }, { headers })),
            catchError((error) => {
                console.error('InvitationService > declineInvitation error:', error);
                throw error;
            })
        );
    }

    // ================================================================
    // In-app accept surface (Phase B.4)
    // ================================================================

    /**
     * Live stream of pending invitations addressed to the given user.
     *
     * Filters `invitations/` where `targetUid == uid` AND `status == 'pending'`.
     * Powers the notification-bell Accept/Decline cards. The composite query
     * relies on the existing `targetUid` index added in B.1 + the B.4 backfill
     * script (`backfill-invitation-target-uid.js`) for legacy rows.
     */
    streamPendingInvitationsForUser(uid: string): Observable<InvitationDocument[]> {
        if (!uid) return of([] as InvitationDocument[]);
        return this.db
            .collection<InvitationDocument>('invitations', (ref) =>
                ref.where('targetUid', '==', uid).where('status', '==', 'pending')
            )
            .valueChanges({ idField: 'key' })
            .pipe(
                map((list) => list as InvitationDocument[]),
                catchError((err) => {
                    console.error('InvitationService > streamPendingInvitationsForUser error:', err);
                    return of([] as InvitationDocument[]);
                })
            );
    }
}
